<template>
  <teleport to="body">
    <div
      v-if="visible"
      ref="menu"
      class="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px]"
      :style="menuStyle"
      @click.stop
    >
      <!-- Normal actions -->
      <template v-if="!confirmingDelete">
        <div class="py-1">
          <button
            v-for="action in actions"
            :key="action.key"
            class="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            :class="action.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'"
            :disabled="action.disabled"
            @click="onAction(action.key)"
          >
            <FontAwesomeIcon :icon="action.icon" class="w-3.5 text-center" />
            {{ action.label }}
          </button>
        </div>
      </template>

      <!-- Delete confirmation -->
      <template v-else>
        <div class="p-3 space-y-2">
          <div class="text-xs font-medium text-gray-900">
            Delete "{{ item.title }}"?
          </div>
          <div v-if="hasChildren" class="text-[11px] text-gray-500">
            Has {{ item.children_count }} {{ item.children_count === 1 ? 'child' : 'children' }}
          </div>
          <div class="flex flex-col gap-1">
            <button
              v-if="hasChildren"
              class="text-xs text-left px-2 py-1.5 rounded border border-gray-200 text-gray-700 hover:bg-gray-50"
              @click="$emit('delete-promote', item); $emit('close')"
            >
              <FontAwesomeIcon :icon="['fas', 'arrow-up']" class="mr-1 text-gray-400" />
              Move children up, then delete
            </button>
            <button
              class="text-xs text-left px-2 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
              @click="$emit('delete', item); $emit('close')"
            >
              <FontAwesomeIcon :icon="['fas', 'trash']" class="mr-1" />
              {{ hasChildren ? 'Delete everything' : 'Delete' }}
            </button>
            <button
              class="text-xs text-center text-gray-400 hover:text-gray-600 py-0.5"
              @click="confirmingDelete = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </template>
    </div>
  </teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { CHILD_TYPES_FOR_PARENT, VALID_PARENT_TYPES } from './support_constants.js';

export default {
  name: 'Support_ContextMenu',
  components: { FontAwesomeIcon },
  props: {
    visible: { type: Boolean, default: false },
    x: { type: Number, default: 0 },
    y: { type: Number, default: 0 },
    item: { type: Object, default: null },
  },
  emits: ['close', 'add-child', 'change-parent', 'copy-link', 'delete', 'delete-promote'],
  data() {
    return { confirmingDelete: false };
  },
  computed: {
    hasChildren() {
      return this.item?.children_count > 0;
    },
    // Driven by the row's own type rather than which tab you were on: in the
    // merged work list there is no tab to ask. A Bug takes an Epic or Feature
    // parent but is a leaf itself, so Change Parent stays live and Add Child
    // does not.
    canAddChild() {
      return !!CHILD_TYPES_FOR_PARENT[this.item?.requirement_type_name];
    },
    canChangeParent() {
      return !!VALID_PARENT_TYPES[this.item?.requirement_type_name];
    },
    actions() {
      return [
        {
          key: 'add-child',
          label: 'Add Child',
          icon: ['fas', 'plus'],
          disabled: !this.canAddChild,
        },
        {
          key: 'change-parent',
          label: 'Change Parent...',
          icon: ['fas', 'arrow-up'],
          disabled: !this.canChangeParent,
        },
        {
          key: 'copy-link',
          label: 'Copy Link',
          icon: ['fas', 'link'],
        },
        {
          key: 'confirm-delete',
          label: 'Delete',
          icon: ['fas', 'trash'],
          danger: true,
        },
      ];
    },
    menuStyle() {
      const menuWidth = 220;
      const menuHeight = this.confirmingDelete ? 140 : this.actions.length * 36;
      const left = this.x + menuWidth > window.innerWidth ? this.x - menuWidth : this.x;
      const top = this.y + menuHeight > window.innerHeight ? this.y - menuHeight : this.y;
      return { left: left + 'px', top: top + 'px' };
    },
  },
  watch: {
    visible(v) {
      if (v) {
        this.confirmingDelete = false;
        setTimeout(() => document.addEventListener('click', this._close), 0);
      } else {
        document.removeEventListener('click', this._close);
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this._close);
  },
  methods: {
    onAction(key) {
      if (key === 'confirm-delete') {
        this.confirmingDelete = true;
        return;
      }
      this.$emit(key, this.item);
      this.$emit('close');
    },
    _close() {
      this.$emit('close');
    },
  },
};
</script>
